import React, { useState, useCallback } from "react";
import MainLayout from "../../components/layout/MainLayout";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { CircularProgress } from "@mui/material";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const DrawerHeader = styled("div")(({ theme }) => ({
    ...theme.mixins.toolbar,
}));

const EmailCompliance = () => {
    const [openUploadModal, setOpenUploadModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState("");
    const navigate = useNavigate();

    const onDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles.length > 0) {
            setSelectedFile(acceptedFiles[0]);
            setMessage("");
        }
    }, []);

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: { "application/pdf": [] },
        multiple: true,
    });

    const handleUpload = async () => {
        if (!selectedFile) {
            setMessage("Please select a PDF file first.");
            return;
        }

        const formData = new FormData();
        formData.append("pdf", selectedFile);

        try {
            setUploading(true);
            const response = await axios.post(
                `${process.env.REACT_APP_PYTHON_BACKEND_URL}/analyze`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            if (response.status === 200) {
                setMessage(`Upload successful: ${response.data.message}`);
                console.log(response.data.responseID);
                navigate(`/details-email?responseID=${response.data.responseID}`);
            }
        } catch (error) {
            setMessage("Upload failed. Please try again.");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="h-[var(--app-height)] overflow-hidden">
            <Box className="flex h-full">
                {/* Sidebar */}
                <MainLayout />

                {/* Main content */}
                <Box component="main" className="flex-1 bg-white px-6 py-4 overflow-hidden">
                    <DrawerHeader />

                    {/* Header */}
                    <div className="flex items-start justify-between border-b border-gray-200 pb-4">
                        {/* Left */}
                        <div>
                            <div className="flex items-center gap-2">
                                <div className="bg-[#C800DE] p-[4px] mt-[-9px] inline-flex items-center justify-center cursor-pointer rounded-full">
                                    <ArrowBackIcon sx={{ fontSize: "8px", color: "#fff" }} />
                                </div>
                                <h1 className="text-lg font-semibold text-[#656565]">
                                    Email Compliance Analysis
                                </h1>
                            </div>
                            {/* <p className="text-xs text-[#656565] mt-1">
                                Document processed on Dec 22, 2024 at 2:34 PM
                            </p> */}
                        </div>

                        {/* Right buttons */}
                        <div className="flex items-center gap-2">
                            <button
                                disabled
                                className="flex items-center gap-2 px-2 py-2 text-sm border border-[#818181] rounded-[12px] text-[#818181] cursor-not-allowed opacity-50"
                            >
                                <VisibilityOutlinedIcon sx={{ fontSize: "14px" }} />
                                Preview
                            </button>

                            <button
                                disabled
                                className="flex items-center gap-2 px-2 text-[12px] py-2 text-sm border border-[#818181] rounded-[12px] text-[#818181] cursor-not-allowed opacity-50"
                            >
                                <ShareOutlinedIcon sx={{ fontSize: "14px" }} />
                                Share
                            </button>

                            <button
                                disabled
                                className="flex items-center gap-2 px-2 py-2 text-sm border border-[#818181] rounded-[12px] text-[#818181] cursor-not-allowed opacity-50"
                            >
                                <FileDownloadOutlinedIcon sx={{ fontSize: "14px" }} />
                                Export
                            </button>

                            <button
                                onClick={() => setOpenUploadModal(true)}
                                className="flex items-center gap-2 px-2 py-2 text-sm font-medium text-white bg-[#bb1ccc] rounded-lg hover:bg-[#a91bb5]"
                            >
                                <UploadFileIcon sx={{ fontSize: "14px" }} />
                                Upload PDF
                            </button>
                        </div>
                    </div>
                </Box>
            </Box>

            <Dialog
                open={openUploadModal}
                onClose={() => {
                    setOpenUploadModal(false);
                    setSelectedFile(null);
                    setMessage("");
                }}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        mt: 10,
                        ml: 20,
                    },
                }}
            >
                <DialogContent>
                    <div className="p-4">
                        <h2 className="text-lg font-semibold text-[#1a1a1a]">
                            Upload PDF, DOC
                        </h2>



                        <div
                            {...getRootProps()}
                            className="mt-6 flex flex-col items-center justify-center
                                       border-2 border-dashed border-gray-300 rounded-xl
                                       h-[140px] cursor-pointer text-gray-500"
                        >
                            <input {...getInputProps()} />
                            <span>
                                {selectedFile ? "File selected" : "Drop or Upload your PDF here"}
                            </span>
                        </div>

                        {selectedFile && (
                            <p className="mt-2 text-sm text-[#bb1ccc]">
                                File Name:{" "}
                                <span className="font-medium">{selectedFile.name}</span>
                            </p>
                        )}

                        {message && (
                            <p className="mt-2 text-sm text-green-600">
                                {message}
                            </p>
                        )}
                    </div>
                </DialogContent>

                <DialogActions className="px-6 pb-4">
                    <button
                        onClick={() => {
                            setOpenUploadModal(false);
                            setSelectedFile(null);
                            setMessage("");
                        }}
                        className="px-6 py-2 border border-gray-300 rounded-lg text-sm"
                        disabled={uploading}
                    >
                        Cancel
                    </button>

                    <button
                        onClick={handleUpload}
                        disabled={uploading || !selectedFile}
                        className="px-6 py-2 bg-[#bb1ccc] text-white rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {uploading ? (
                            <CircularProgress size={20} sx={{ color: "white" }} />
                        ) : (
                            "Submit"
                        )}
                    </button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default EmailCompliance;